// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Search from "@insite/mobius/Icons/Search";
import MobiusLink, { LinkPresentationProps } from "@insite/mobius/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { getProp, safeColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getLinkTypeField, { LinkType, linkTypeOptions } from "@store/Framework/Common/Fields/Toolkit/linkType";
import AccordionSection from "@store/MobiusOverrides/AccordionSection/AccordionSection";
import { useAnalytics } from "@store/Framework/Common/Hooks/useAnalytics";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import parse from "html-react-parser";
import React, { useState } from "react";
import { css } from "styled-components";

const enum fields {
    widgetTitle = "widgetTitle",
    searchPlaceHolder = "searchPlaceHolder",
    resultsMessage = "resultsMessage",
    noResultsMessage = "noResultsMessage",
    useSheetImport = "useSheetImport",
    sheetUrl = "sheetUrl",
    useAccordions = "useAccordions",
    sections = "sections",
    title = "title",
    overrideAccordionColors = "overrideAccordionColors",
    accordionBackgroundColor = "accordionBackgroundColor",
    accordionTextColor = "accordionTextColor",
    documents = "documents",
    linkType = "linkType",
    fileUrl = "fileUrl",
    destination = "destination",
    label = "label",
    skus = "skus",
}
const linkTypeField = getLinkTypeField(fields.linkType);

type DocumentModel = {
    fields: {
        [fields.linkType]: LinkType;
        [fields.fileUrl]: string;
        [fields.destination]: LinkFieldValue;
        [fields.label]: string;
        [fields.skus]: string;
    };
};

type DocumentSearchResultModel = {
    matchedSkus: string;
    matchedLabel: string;
} & DocumentModel;

type SectionModel = {
    fields: {
        title: string;
        overrideAccordionColors: boolean;
        accordionBackgroundColor: string;
        accordionTextColor: string;
        documents: DocumentModel[];
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        widgetTitle: string;
        searchPlaceHolder: string;
        resultsMessage: string;
        noResultsMessage: string;
        useSheetImport: boolean;
        sheetUrl: string;
        useAccordions: string;
        sections: SectionModel[];
    };
}

type Props = OwnProps;

export interface DocumentationCenterStyles {
    wrapper?: InjectableCss;
    headWrapper?: InjectableCss;
    title?: TypographyProps;
    fieldWrapper?: InjectableCss;
    searchField?: TextFieldPresentationProps;
    searchResultsWrapper?: InjectableCss;
    sectionWrapper?: InjectableCss;
    sectionTitle?: TypographyProps;
    link?: LinkPresentationProps;
    accordion?: AccordionPresentationProps;
    anchoredAccordion?: AccordionSectionPresentationProps;
}

export const documentationCenterStyles: DocumentationCenterStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(6)};
        `,
    },
    headWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            gap: ${pxToRem(5)};
            padding: ${pxToRem(46, 0)};
            border-bottom: 2px solid ${getColor("primary.main")};

            ${breakpoint("sm", "down")} {
                flex-direction: column;
                align-items: flex-start;
                gap: ${pxToRem(10)};
                padding: ${pxToRem(25, 0)};
            }
        `,
    },
    title: {
        variant: "h3",
        css: css`
            margin: 0;
        `,
    },
    fieldWrapper: {
        css: css`
            width: ${pxToRem(400)};

            ${breakpoint("sm", "down")} {
                width: 100%;
            }
        `,
    },
    searchField: {
        iconProps: {
            src: Search,
        },
    },
    searchResultsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(10)};
            max-height: 60vh;
            overflow-y: auto;
            padding: ${pxToRem(20)};
            margin: ${pxToRem(15, -20, 0)};
            background: ${getColor("common.borderLight")};
            border-radius: 5px;
            box-shadow: ${getProp("theme.shadows.1")};
        `,
    },
    sectionWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(10)};
            padding: ${pxToRem(20, 0)};
            border-bottom: 1px solid ${getColor("common.borderDark")};
        `,
    },
    sectionTitle: {
        variant: "h4",
    },
    accordion: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(5)};
            margin: ${pxToRem(15, 0)};
        `,
    },
    anchoredAccordion: {
        mergeCss: true,
    },
};

const styles = documentationCenterStyles;

const DocumentationCenter = ({
    fields: { widgetTitle, searchPlaceHolder, resultsMessage, noResultsMessage, useAccordions, sections },
    id: widgetIdentifier,
}: Props) => {
    const [search, setSearch] = useState("");
    const groupedDocuments = sections.reduce<DocumentModel[]>((acc, item) => acc.concat(item.fields.documents), []);
    const links = useGetLinks(groupedDocuments, o => o.fields.destination);
    const { dispatchDocumentCenterClick, dispatchDocumentCenterUseSearch, dispatchDocumentCenterSectionExpandClick } =
        useAnalytics();

    const getLink = (id: string) => {
        return links.find(link => "id" in link && link.id === id);
    };

    const getCategoryLink = (id: string) => {
        let index;

        for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
            const section = sections[sectionIndex];
            const docIndex = section.fields.documents.findIndex(doc => doc.fields.destination.value === id);
            if (docIndex !== -1) {
                index =
                    sections.slice(0, sectionIndex).reduce((acc, curr) => acc + curr.fields.documents.length, 0) +
                    docIndex;
            }
        }

        return index ? links[index].url : "";
    };

    const searchChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
    };

    const searchResults: DocumentSearchResultModel[] = groupedDocuments
        .map(({ fields: docFields }) => {
            const label = docFields.label.toLowerCase();
            const skus = docFields.skus
                ?.toLowerCase()
                .split(",")
                .filter(sku => sku.trim() !== "");

            const matchLabel = label.includes(search.toLowerCase());
            const matchSkus = skus?.some(sku => sku.includes(search.toLowerCase()));

            let matchedLabel = docFields.label;
            if (matchLabel) {
                const regex = new RegExp(search, "gi");
                matchedLabel = docFields.label.replace(regex, match => `<mark>${match}</mark>`);
            }

            let matchedSkus = "";
            if (matchSkus) {
                matchedSkus = skus
                    .map(sku => {
                        if (sku.includes(search.toLowerCase())) {
                            return sku.replace(new RegExp(search, "gi"), match => `<mark>${match}</mark>`);
                        }
                        return null;
                    })
                    .filter(sku => sku !== null)
                    .join(", ");
                matchedSkus = `(${translate("part#")} ${matchedSkus})`;
            }

            if (matchLabel || matchSkus) {
                return {
                    matchedSkus,
                    matchedLabel: `<b>${matchedLabel}</b>`,
                    fields: {
                        ...docFields,
                    },
                };
            }

            return null;
        })
        .filter(document => document !== null) as DocumentSearchResultModel[];

    const DocumentComponent = ({ document }: { document: DocumentModel | DocumentSearchResultModel }) => {
        const documentFields = document.fields;
        const isFile = documentFields.linkType === "file";
        const isCategory = documentFields.linkType === "destination" && documentFields.destination.type === "Category";
        const isUrl = documentFields.linkType === "destination" && documentFields.destination.type === "Url";

        const url = isFile
            ? documentFields.fileUrl
            : isCategory
            ? getCategoryLink(documentFields.destination.value)
            : isUrl
            ? documentFields.destination.value
            : getLink(documentFields.destination.value)?.url || "";

        if (!url) {
            return null;
        }

        return (
            <MobiusLink
                className="link"
                {...styles.link}
                href={url}
                target="_blank"
                onClick={() => dispatchDocumentCenterClick(documentFields.label, url)}
            >
                {parse("matchedLabel" in document ? document.matchedLabel : documentFields.label, parserOptions)}{" "}
                {"matchedSkus" in document ? parse(document.matchedSkus, parserOptions) : ""}
            </MobiusLink>
        );
    };

    const SectionTitle = ({ message }: { message: string }) => (
        <Typography {...styles.sectionTitle}>{message}</Typography>
    );

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.headWrapper}>
                <Typography {...styles.title}>{widgetTitle}</Typography>
                <StyledWrapper {...styles.fieldWrapper}>
                    <TextField
                        {...styles.searchField}
                        placeholder={searchPlaceHolder}
                        value={search || ""}
                        onChange={searchChangeHandler}
                        onClick={() => dispatchDocumentCenterUseSearch(widgetTitle)}
                    />
                </StyledWrapper>
            </StyledWrapper>
            {search && (
                <>
                    <SectionTitle message={`${resultsMessage} (${searchResults.length.toString()})`} />
                    <StyledWrapper {...styles.searchResultsWrapper}>
                        {searchResults.length > 0 ? (
                            searchResults.map((document, index) => (
                                <DocumentComponent
                                    key={`sr-${widgetIdentifier}-${document.fields.destination.value}-${index}`}
                                    document={document}
                                />
                            ))
                        ) : (
                            <Typography>{`${noResultsMessage} ${search}`}</Typography>
                        )}
                    </StyledWrapper>
                </>
            )}
            {useAccordions ? (
                <Accordion headingLevel={3} {...styles.accordion}>
                    {sections.map(
                        (
                            {
                                fields: {
                                    overrideAccordionColors,
                                    accordionBackgroundColor,
                                    accordionTextColor,
                                    title,
                                    documents,
                                },
                            },
                            index,
                        ) => {
                            const accordionStyles: AccordionSectionPresentationProps = {
                                ...styles.anchoredAccordion,
                                headerProps: {
                                    css: css`
                                        button {
                                            ${overrideAccordionColors && accordionBackgroundColor
                                                ? `
                                                    background-color: ${accordionBackgroundColor} !important;

                                                    &[aria-expanded="true"], &:hover, &:focus {
                                                        background-color: ${safeColor(accordionBackgroundColor).darken(
                                                            0.1,
                                                        )} !important;
                                                    }
                                                `
                                                : ""}

                                            ${overrideAccordionColors && accordionTextColor
                                                ? `
                                                    color: ${accordionTextColor} !important;

                                                    svg {
                                                        color: ${accordionTextColor} !important;
                                                    }
                                                `
                                                : ""}
                                        }
                                    `,
                                },
                            };

                            return (
                                <AccordionSection
                                    key={`${title}-${index}`}
                                    title={title}
                                    onTogglePanel={() => dispatchDocumentCenterSectionExpandClick(widgetTitle, title)}
                                    {...accordionStyles}
                                >
                                    <StyledWrapper {...styles.sectionWrapper}>
                                        {documents.map((document, index) => (
                                            <DocumentComponent
                                                key={`${document.fields.destination.value}-${index}`}
                                                document={document}
                                            />
                                        ))}
                                    </StyledWrapper>
                                </AccordionSection>
                            );
                        },
                    )}
                </Accordion>
            ) : (
                sections.map(({ fields: { title, documents } }, index) => (
                    <StyledWrapper key={`${title}-${index}`} {...styles.sectionWrapper}>
                        <SectionTitle message={title} />
                        {documents.map((document, index) => (
                            <DocumentComponent
                                key={`${widgetIdentifier}-${document.fields.destination.value}-${index}`}
                                document={document}
                            />
                        ))}
                    </StyledWrapper>
                ))
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: DocumentationCenter,
    definition: {
        group: "Documentation" as any,
        displayName: "Documentation Center",
        fieldDefinitions: [
            {
                name: fields.widgetTitle,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "Documentation Center",
                isRequired: true,
            },
            {
                name: fields.searchPlaceHolder,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "Find by name, keywords or part#",
                isRequired: true,
            },
            {
                name: fields.resultsMessage,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "Search result(s)",
                isRequired: true,
            },
            {
                name: fields.noResultsMessage,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "No matching results found for:",
                isRequired: true,
            },
            // For a potential import from CSV
            // {
            //     name: fields.useSheetImport,
            //     editorTemplate: "CheckboxField",
            //     fieldType: "Contextual",
            //     defaultValue: false,
            // },
            // {
            //     name: fields.sheetUrl,
            //     displayName: "Sheet Import (csv, xsl, xslx)",
            //     fieldType: "Contextual",
            //     editorTemplate: "FilePickerField",
            //     defaultValue: "",
            //     isVisible: x => x.fields.useSheetImport,
            // },
            {
                name: fields.useAccordions,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
            },
            {
                name: fields.sections,
                displayName: "Section(s)",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title || translate("Section");
                },
                defaultValue: [],
                fieldType: "General",
                isVisible: x => !x.fields.useSheetImport,
                fieldDefinitions: [
                    {
                        name: fields.title,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: fields.overrideAccordionColors,
                        editorTemplate: "CheckboxField",
                        tooltip: "Only applicable if Use Accordion is checked.",
                        defaultValue: false,
                    },
                    {
                        name: fields.accordionBackgroundColor,
                        displayName: "Accordion Background Color",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                        isVisible: x => x.fields.overrideAccordionColors,
                    },
                    {
                        name: fields.accordionTextColor,
                        displayName: "Accordion Text Color",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                        isVisible: x => x.fields.overrideAccordionColors,
                    },
                    {
                        name: fields.documents,
                        displayName: "Document(s)",
                        editorTemplate: "ListField",
                        getDisplay: (item: HasFields) => {
                            return item.fields.label || translate("Document");
                        },
                        defaultValue: [],
                        fieldDefinitions: [
                            {
                                ...linkTypeField.definition,
                            },
                            {
                                name: fields.fileUrl,
                                displayName: "File",
                                editorTemplate: "FilePickerField",
                                defaultValue: "",
                                isRequired: true,
                                isVisible: x => x.fields.linkType === linkTypeOptions.file.id,
                            },
                            {
                                name: fields.destination,
                                editorTemplate: "LinkField",
                                defaultValue: { type: "Page", value: "" },
                                isRequired: true,
                                isVisible: x => x.fields.linkType === linkTypeOptions.destination.id,
                            },
                            {
                                name: fields.label,
                                editorTemplate: "TextField",
                                defaultValue: "",
                                isRequired: true,
                            },
                            {
                                name: fields.skus,
                                displayName: "Sku(s) separated by comma",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                        ],
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
